import { unreachable } from './util';
import type { FileType } from './modelTypes';

type PropPath = (number | string)[];

/**
 * Parses a string path into an array of properties, handling both dot and bracket notation.
 * - Properties inside brackets are treated as strings unless they represent a number.
 * - Empty brackets (`[]`) are represented as `-1`.
 * - Whitespace is ignored around properties and brackets.
 *
 * @param {string} path - The string path to parse, e.g., 'foo[bar][4][name]'.
 * @returns {PropPath} - An array representing the path, with bracketed numbers as numbers and empty brackets as `-1`.
 *
 * @example
 * parsePath('foo[bar][4][name]'); // ['foo', 'bar', 4, 'name']
 * parsePath('foo[]'); // ['foo', -1]
 * parsePath('foo[ bar ][baz][ 7 ][test]'); // ['foo', 'bar', 'baz', 7, 'test']
 * parsePath('   foo[  ][ bar ][4 ]'); // ['foo', -1, 'bar', 4]
 */
export function parsePath(path: string): PropPath {
	return path.split('[').map((p) => {
		const part = p.replace(/]/g, '').trim() || '-1';
		const asNum = parseInt(part);
		return isNaN(asNum) ? part : asNum;
	});
}

/**
 * Turn a parsed path back into a path string
 * @param path the path to render
 * @returns the rendered path
 * @example
 * renderPath(['name']) // name
 * renderPath(['name', 'first']) // name[first]
 * renderPath(['countries', 0, 'code']) // countries[0][code]
 */
export function renderPath(path: PropPath): string {
	if (path.length === 0) return '';
	let res = `${path[0]}`;
	for (const part of path.slice(1)) {
		res += `[${part}]`;
	}
	return res;
}

type IndexMemory = WeakMap<any[], Map<any, number>>;
/**
 * Creates a new memory object used to track insertions in arrays for paths containing `-1` indices.
 * This allows `assignPath` to consistently insert into the same array elements when multiple insertions
 * are made to the same logical path (e.g., `people[].name` and `people[].age`).
 *
 * @returns {IndexMemory} - The memory object used for tracking array indices during path assignment.
 */
export function createMemory(): IndexMemory {
	return new WeakMap();
}

/**
 * Retrieves the index from the memory for the given target array and property name.
 * This is used when assigning to arrays with a `-1` index to determine the next object or element
 * to insert into. If the property has not yet been inserted, it will create a new entry.
 *
 * @param {IndexMemory} memory - The memory tracking object.
 * @param {any[]} target - The target array for which the index is being retrieved.
 * @param {string|number} propName - The property name associated with the index in the array.
 * @returns {number} - The index to insert the next value into the array.
 */
export function indexFromMemory(memory: IndexMemory, target: any[], propName: string | number) {
	let prevIndices = memory.get(target);
	let i = 0;
	if (!prevIndices) {
		prevIndices = new Map([[propName, target.length + 1]]);
		memory.set(target, prevIndices);
		i = target.length;
	} else {
		i = prevIndices.get(propName) ?? 0;
		// we store what we *think* will be the next index. e.g. 1 if we just inserted the initial item
		// but if the array is mixed, the next inserted item may not be an object
		// so in the cases where we are given an existing index
		// we should first check if it is actually available and increment if this is not the case
		while (target[i] !== undefined && typeof target[i] !== 'object') i++;

		prevIndices.set(propName, i + 1);
	}

	return i;
}

/**
 * Assigns a value to a nested property or array based on the provided path.
 * Handles `-1` indices by inserting into the next available object or array element.
 *
 * - If a `-1` index is found, the function will either push to the array (for direct array insertions)
 * or insert into the next available object in the array based on the memory.
 *
 * @param {Record<string, any> | any[]} target - The target object or array to assign the value to.
 * @param {PropPath} path - The path of properties to assign the value to, where `-1` represents an insertion point.
 * @param {any} value - The value to assign at the specified path.
 * @param {IndexMemory} [memory] - Optional memory that tracks insertions into arrays with `-1` indices for consistent updates.
 */
export function assignPath(target: Record<string, any> | any[], path: PropPath, value: any, memory?: IndexMemory) {
	if (path.length === 0) return;

	for (let i = 0; i < path.length; i++) {
		let part = path[i];
		const nextPart = path[i + 1];
		const isLast = i == path.length - 1;

		if (part === -1 && Array.isArray(target) && nextPart) {
			if (memory) {
				part = indexFromMemory(memory!, target, nextPart);
			} else {
				throw new Error('-1 object insertion not possible without memory.');
			}
		}

		if (!isLast) {
			const t = target as Record<string | number, any>;
			if (typeof nextPart === 'number') {
				if (!t[part]) t[part] = [];
			} else {
				if (!t[part]) t[part] = {};
			}
			target = t[part];
		} else {
			if (part === -1 && Array.isArray(target)) {
				target.push(value);
			} else {
				(target as Record<string | number, any>)[part] = value;
			}
		}
	}
}

/**
 * Parses a given HTML form element and returns an object
 * with key-value pairs representing form field names and their values.
 *
 * @param {HTMLFormElement} form - The HTML form element to parse.
 * @returns {Record<string, any>} An object where the keys are the form field names
 * and the values are the corresponding field values.
 */
export async function parseForm<T>(
	form: HTMLFormElement,
	getFileValue: (el: HTMLInputElement) => Promise<T>,
): Promise<Record<string, any>> {
	const parsedObj = {};
	const memory = createMemory();

	for (const element of form.elements) {
		if (
			!(element instanceof HTMLInputElement) &&
			!(element instanceof HTMLTextAreaElement) &&
			!(element instanceof HTMLSelectElement)
		) {
			continue;
		}

		if (element.hasAttribute('readonly')) {
			continue;
		}

		const path = parsePath(element.name);

		let value: string | number | boolean | T | null | undefined = undefined;

		const isPartOfArray = typeof path.at(-1) === 'number';

		if (element instanceof HTMLInputElement) {
			if (element.type === 'checkbox') {
				if (isPartOfArray) {
					// this is a checkbox array, we want to store the "value" attribute instead of just the checked true/false state

					// not checked, don't add this value to the array and move on directly
					if (!element.checked) continue;

					value = element.getAttribute('value');
				} else {
					value = element.checked;
				}
			} else if (element.type === 'file') {
				value = await getFileValue(element);
			} else if (element.type === 'number') {
				value = element.valueAsNumber;
				if (isNaN(value)) value = undefined;
			} else if (element.value.length) {
				value = element.value;
			}
		} else if (element instanceof HTMLTextAreaElement) {
			if (element.value.length > 0) {
				value = element.value;
			}
		} else if (element instanceof HTMLSelectElement) {
			value = element.value;
		} else {
			unreachable(element);
		}

		if (value !== undefined) {
			assignPath(parsedObj, path, value, memory);
		}
	}

	return parsedObj;
}
